import '@aws-amplify/ui-react-geo/styles.css';

import { useMemo } from 'react';

import { LoadingIndicator } from '@bambi/component-library/src/components/LoadingIndicator/LoadingIndicator';
import { Tabs } from '@bambi/component-library/src/components/Tabs/Tabs';
import { SerializedError } from '@reduxjs/toolkit';
import { FetchBaseQueryError } from '@reduxjs/toolkit/query';
import chroma from 'chroma-js';
import { useParams } from 'react-router';

import { useLocationsLocationLogEntryTripsListQuery } from 'src/common/external/bambi-api/bambiApi';
import { formatRTKQueryError } from 'src/common/util/formatRTKQueryError';
import { useUserRoles } from 'src/features/auth/useUserRoles';

import { EventDeviceLogDataGrid } from './EventDeviceLog/EventDeviceLogDataGrid';
import {
  EventDeviceLogMapView,
  LocationWithColor,
} from './EventDeviceLog/EventDeviceLogMapView';
import { TripSummaryItemList } from './TripSummaryItemList';
import { TripSummaryMapView } from './TripSummaryMapView';
import { useTripSummaryRouteViewModel } from './useTripSummaryRouteViewModel';

export function TripSummaryRoute() {
  const { isBambi } = useUserRoles();

  if (!isBambi) {
    return <TripSummaryTabContent />;
  }

  const tabs = [
    {
      label: 'Events',
      value: 'events',
      content: <TripSummaryTabContent />,
    },
    {
      label: 'Event Device Log',
      value: 'event-device-log',
      content: <EventDeviceLogTabContent />,
    },
  ];
  return <Tabs tabs={tabs} defaultValue="events" />;
}

function TripSummaryTabContent() {
  const { shouldShowLoadingState, errors, tripId } =
    useTripSummaryRouteViewModel();

  return (
    <div className="grid min-h-[90vh] sm:grid-cols-12">
      <div className="col-span-5 h-[90vh] overflow-y-auto overflow-x-hidden lg:col-span-3">
        {errors.length
          ? errors.map((error) => (
              <TripSummaryError error={error} tripId={tripId || ''} />
            ))
          : null}
        {shouldShowLoadingState ? (
          <div className="flex items-center justify-center p-2">
            <LoadingIndicator />
          </div>
        ) : (
          <TripSummaryItemList />
        )}
      </div>
      <div className="col-span-7 h-full lg:col-span-9">
        <TripSummaryMapView />
      </div>
    </div>
  );
}

function EventDeviceLogTabContent() {
  const { tripId } = useParams<{ tripId: string }>();
  const { data: eventDeviceLog } = useLocationsLocationLogEntryTripsListQuery(
    {
      tripId: tripId!,
    },
    {
      skip: !tripId,
    }
  );

  const scale = chroma.scale(['#AC0C12', '#FFFFFF', '#0CACA6']);
  const locationsWithColor: LocationWithColor[] = useMemo(
    () =>
      eventDeviceLog?.results.map((location, i) => ({
        ...location,
        color: scale(i / (eventDeviceLog.count || 1)).hex(),
      })) ?? ([] as LocationWithColor[]),
    [eventDeviceLog, scale]
  );

  if (!tripId) {
    return <div>No trip id</div>;
  }

  return (
    <div className="grid min-h-[90vh] sm:grid-cols-12">
      <div className="col-span-5 h-[90vh] overflow-y-auto overflow-x-hidden lg:col-span-3">
        <EventDeviceLogDataGrid
          locations={locationsWithColor}
          totalCount={eventDeviceLog?.count ?? 0}
        />
      </div>
      <div className="col-span-7 h-full lg:col-span-9">
        <EventDeviceLogMapView locations={locationsWithColor.reverse()} />
      </div>
    </div>
  );
}

interface TripSummaryErrorProps {
  error: FetchBaseQueryError | SerializedError;
  tripId: string;
}
function TripSummaryError({ error, tripId }: TripSummaryErrorProps) {
  const renderedError = formatRTKQueryError(error);
  return (
    <div className="p-4" key={renderedError}>
      <h2 className="text-lg font-medium">
        Error loading events for trip id {tripId}
      </h2>
      {renderedError}
    </div>
  );
}
